import {
  faArrowUp,
  faBoltLightning,
  faLightbulb,
  faXmark,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { chain } from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { KeywordTableColumnTypes } from '../../../components/muiLegacy/Tables/KeywordTable/KeywordTableColumnPicker/models';
import { KeywordTableState } from '../../../components/muiLegacy/Tables/KeywordTable/logic/useKeywordTableState';
import VoxelAlert from '../../../components/voxel/Alert/VoxelAlert';
import VoxelButton from '../../../components/voxel/Buttons/VoxelButton';
import VoxelIconButton from '../../../components/voxel/Buttons/VoxelIconButton';
import VoxelSelect from '../../../components/voxel/Forms/VoxelSelect';
import VoxelText from '../../../components/voxel/Text/VoxelText';
import { ProjectGq } from '../../../graphql/projects/models/ProjectGq';
import { TagGroupStatsGq } from '../../../graphql/projects/queries/useGetTagGroupStatsGq';
import AutotagDialog from '../TagSection/Dialogs/AutotagDialog';
import { AddTagGroupButton } from './AddTagGroupButton';
import AddTagsButton from './AddTagsButton';
import EditTagGroup from './EditTagGroup';
import RemoveTagButton from './RemoveTagButton';
import Tag from './Tag';
import ToggleTagGroupColumn from './ToggleTagGroupColumn';
import DeleteAllTagGroupsButton from './DeleteAllTagGroupsButton';

export interface TagSectionBetaProps {
  project: ProjectGq;
  reload: () => Promise<unknown>;
  keywordTableState: KeywordTableState;
  tagGroupStats?: TagGroupStatsGq;
}

export const TagSectionBeta: React.FC<TagSectionBetaProps> = ({
  project,
  reload,
  keywordTableState,
  tagGroupStats,
}) => {
  // Selected tag group
  const selectedTagGroupId = keywordTableState.tableUIState.shownTagPanelId;
  const setSelectedTagGroupId = (id: number) =>
    keywordTableState.setTableUIState((prev) => ({ ...prev, shownTagPanelId: id }));
  const selectedTagGroup = project.tagGroups.find((tg) => tg.id === selectedTagGroupId);

  // Change tag group if it doesn't exist
  useEffect(() => {
    if (!!project.tagGroups?.length && !selectedTagGroup) {
      setSelectedTagGroupId(project?.tagGroups[0]?.id);
    }
  }, [selectedTagGroupId, project]);

  // Auto tag
  const [showAutoTag, setShowAutoTag] = useState(false);

  // Warnings
  const taggedKeywords = useMemo(
    () =>
      chain(tagGroupStats.tagStats)
        .filter((t) => t.tagGroupId === selectedTagGroupId)
        .sumBy((t) => t.keywordCount ?? 0)
        .value(),
    [selectedTagGroupId, tagGroupStats]
  );

  const noTagGroups = !project?.tagGroups?.length;
  const noTagGroupSelected = !selectedTagGroup && !noTagGroups;
  const noTags = !noTagGroupSelected && !selectedTagGroup?.tags?.length;

  return (
    <div className="relative h-full">
      <VoxelIconButton
        onClick={() =>
          keywordTableState.setTableUIState((prev) => ({ ...prev, showTagPanel: false }))
        }
        icon={<FontAwesomeIcon icon={faXmark} size="lg" />}
        size="md"
        tooltip="Close"
        color="white"
        className="absolute top-2 right-2"
      />
      <AutotagDialog
        openDialog={showAutoTag}
        setOpenDialog={setShowAutoTag}
        tagGroup={selectedTagGroup}
        hasEnglishTranslation={project.hasEnglishTranslation}
        hasTranslation={project.translated}
        reload={reload}
        onSuccess={() => {
          keywordTableState.columnPickerBag.showTagGroup(selectedTagGroupId);
          const column = keywordTableState.columnPickerBag.columnState.find(
            (c) => c.id === selectedTagGroupId && c.type === KeywordTableColumnTypes.TagGroup
          );
          column && keywordTableState.scrollToColumn(column);
        }}
      />
      <div
        className={`flex flex-row gap-4 items-center pt-[52px] px-6 pb-4 border-b bg-voxel-gray-50`}
      >
        <AddTagGroupButton
          project={project}
          setSelectedTagGroupId={setSelectedTagGroupId}
          reload={reload}
        />
        {project.tagGroups?.length > 7 && (
          <DeleteAllTagGroupsButton
            tagGroups={project.tagGroups}
            reload={reload}
            project={project}
          />
        )}
      </div>
      {selectedTagGroup && !!project.tagGroups.length && (
        <div className="flex flex-col h-full">
          <div className="flex flex-col gap-4 border-b bg-voxel-gray-50 pl-6 pr-3.5 py-3">
            <div className="flex flex-row gap-2 items-end">
              <VoxelSelect
                label="Tag group"
                labelPosition="top"
                options={project.tagGroups.map((tg) => ({ title: tg.name, value: tg.id }))}
                selectedOptionValue={selectedTagGroupId}
                setSelectedOption={(option) => setSelectedTagGroupId(option.value)}
                size="sm"
              />
              <EditTagGroup
                project={project}
                tagGroup={selectedTagGroup}
                setSelectedTagGroupId={setSelectedTagGroupId}
                reload={reload}
              />
            </div>
            <div className="flex flex-row items-center justify-between">
              <AddTagsButton tagGroup={selectedTagGroup} reload={reload} />
              <div className="flex flex-row items-center ">
                <ToggleTagGroupColumn
                  keywordTableState={keywordTableState}
                  tagGroup={selectedTagGroup}
                />
                <VoxelButton
                  size="xs"
                  title="Auto"
                  onClick={() => setShowAutoTag(true)}
                  color={selectedTagGroup?.autotagQueued ? 'primary' : 'transparent'}
                  tooltip={showAutoTag ? undefined : 'Tag keywords automatically'}
                  startIcon={<FontAwesomeIcon size="1x" icon={faBoltLightning} />}
                  submitting={!!selectedTagGroup?.autotagQueued}
                />
              </div>
            </div>
          </div>
          <div className="bg-white h-full max-h-[calc(100vh-279px)] overflow-y-auto flex flex-col gap-6 py-4">
            {/* No tags warning */}
            {!selectedTagGroup?.tags?.length && !selectedTagGroup.generated && (
              <div className="px-6">
                <VoxelAlert severity="success" size="xs" fontAIconName={faArrowUp} iconSize="lg">
                  This tag group has no tags. Add tags by pressing the 'Add tags' button above.
                </VoxelAlert>
              </div>
            )}
            {!!selectedTagGroup?.tags?.length && (
              <div className="flex flex-col gap-2">
                {/* No tagged keywords warning */}
                {!taggedKeywords && !!selectedTagGroup?.tags?.length && (
                  <div className="px-6 pt-0.5">
                    <VoxelAlert
                      severity="success"
                      size="xs"
                      fontAIconName={faLightbulb}
                      iconSize="lg"
                    >
                      Tag keywords by selecting keywords from the keyword table. Then click on the
                      tag below.
                    </VoxelAlert>
                  </div>
                )}
                <div className="px-6 flex flex-row">
                  <VoxelText variant="form-caption" className="flex-grow">
                    Tag
                  </VoxelText>
                  <VoxelText variant="form-caption" align="right">
                    # Keywords
                  </VoxelText>
                </div>
                <div className="ml-5 mr-4">
                  {chain(selectedTagGroup?.tags)
                    .orderBy((t) => t.name)
                    .map((tag) => (
                      <Tag
                        tag={tag}
                        tagGroup={selectedTagGroup}
                        key={tag.id}
                        reload={reload}
                        keywordTableState={keywordTableState}
                        tagGroupStats={tagGroupStats}
                      />
                    ))
                    .value()}
                  {taggedKeywords > 0 && (
                    <div className="mx-1 mt-3">
                      <RemoveTagButton
                        keywordTableState={keywordTableState}
                        tagGroup={selectedTagGroup}
                        reload={reload}
                      />
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default TagSectionBeta;
